import { PortableText as PortableTextRenderer } from '@portabletext/react';
import React from 'react';
import Text from 'components/Text';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { getLinkUrlFromLinkContent } from '@fogia/core-web/utils/internalRefToSlug';
import BlockQuote from './components/BlockQuote';
import Bold from './components/Bold';
import Heading from './components/Heading';
import Italic from './components/Italic';
import Link from './components/Link';
import ListItem from './components/ListItem';
import OrderedList from './components/OrderedList';
import UnorderedList from './components/UnorderedList';

export interface PortableTextProps {
    /** Array of blocks, or a single block (normalized to array internally). */
    value: any[] | any;
    className?: string;
    typography?: string | Array<string | null>;
}

const PortableText: React.FC<PortableTextProps> = ({ value, className, typography }) => {
    const siteReferences = useAppSelector((state) => state.application?.references) ?? {};
    const pageReferences = useAppSelector((state) => state.page?.data?.references) ?? {};
    const references = { ...siteReferences, ...pageReferences };
    const valueArray = Array.isArray(value) ? value : value != null ? [value] : [];
    if (valueArray.length === 0) {
        return null;
    }

    // 🔧 Sanitize Sanity data: API may use 'type'/'key', @portabletext expects '_type'/'_key'
    const normalizedValue = valueArray.map((block) => {
        if (!block || typeof block !== 'object') {
            return block;
        }

        const blockWithType = block.type && !block._type ? { ...block, _type: block.type } : block;
        const blockWithKey =
            blockWithType.key && !blockWithType._key ? { ...blockWithType, _key: blockWithType.key } : blockWithType;
        const blockWithStyle = !blockWithKey.style ? { ...blockWithKey, style: 'normal' } : blockWithKey;

        const sanitizedMarkDefs = Array.isArray(blockWithStyle.markDefs)
            ? blockWithStyle.markDefs.map((def: Record<string, unknown>) => {
                  if (!def || typeof def !== 'object') return def;
                  const withType = def.type && !def._type ? { ...def, _type: def.type } : def;
                  return withType.key && !withType._key ? { ...withType, _key: withType.key } : withType;
              })
            : blockWithStyle.markDefs;

        if (!Array.isArray(blockWithStyle.children)) {
            return { ...blockWithStyle, markDefs: sanitizedMarkDefs };
        }

        const sanitizedChildren = blockWithStyle.children.map((child: Record<string, unknown>) => {
            if (!child || typeof child !== 'object') return child;
            const withType = child.type && !child._type ? { ...child, _type: child.type } : child;
            return withType.key && !withType._key ? { ...withType, _key: withType.key } : withType;
        });

        return {
            ...blockWithStyle,
            children: sanitizedChildren,
            markDefs: sanitizedMarkDefs,
        };
    });

    if (normalizedValue.length === 0) {
        return null;
    }

    return (
        <div className={className}>
            <PortableTextRenderer
                value={normalizedValue}
                components={{
                    block: {
                        normal: ({ children }) => (
                            <Text typography={typography ?? 'Secondary/20_120_400_-1_Indent'} $as="p" mb="16px">
                                {children}
                            </Text>
                        ),
                        h1: ({ children }) => <Heading type="h1">{children}</Heading>,
                        h2: ({ children }) => <Heading type="h2">{children}</Heading>,
                        h3: ({ children }) => <Heading type="h3">{children}</Heading>,
                        h4: ({ children }) => <Heading type="h4">{children}</Heading>,
                        h5: ({ children }) => <Heading type="h5">{children}</Heading>,
                        h6: ({ children }) => <Heading type="h6">{children}</Heading>,
                        blockquote: ({ children }) => <BlockQuote>{children}</BlockQuote>,
                    },
                    list: {
                        bullet: ({ children }) => <UnorderedList>{children}</UnorderedList>,
                        number: ({ children }) => <OrderedList>{children}</OrderedList>,
                    },
                    listItem: {
                        bullet: ({ children }) => <ListItem>{children}</ListItem>,
                        number: ({ children }) => <ListItem>{children}</ListItem>,
                    },
                    marks: {
                        strong: ({ children }) => <Bold>{children}</Bold>,
                        em: ({ children }) => <Italic>{children}</Italic>,
                        link: ({ value, children }) => {
                            const href = getLinkUrlFromLinkContent(value, references);
                            const target =
                                value && typeof value === 'object' && 'target' in value
                                    ? (value as { target?: '_self' | '_blank' }).target
                                    : undefined;
                            return (
                                <Link href={href} target={target}>
                                    {children}
                                </Link>
                            );
                        },
                    },
                }}
            />
        </div>
    );
};

export default PortableText;
